// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as CallCommands from '@app/features/voice/commands/CallCommands';
import {CallNotRingableModal} from '@app/features/voice/components/alerts/CallNotRingableModal';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const logger = new Logger('CallUtils');
export interface CallStartConfirmationOptions {
	bypassConfirm?: boolean;
	silent?: boolean;
	kind?: 'voice' | 'video';
}
export interface CallStartConfirmationEvent {
	shiftKey?: boolean;
}
export function shouldBypassCallStartConfirmation(event?: CallStartConfirmationEvent | null): boolean {
	return Boolean(event?.shiftKey);
}
export function shouldStartCallSilently(event?: CallStartConfirmationEvent | null): boolean {
	return Boolean(event?.shiftKey);
}
export function getCallStartRequestOptions(
	event?: CallStartConfirmationEvent | null,
	options: Pick<CallStartConfirmationOptions, 'kind'> = {},
): CallStartConfirmationOptions {
	return {
		...options,
		bypassConfirm: shouldBypassCallStartConfirmation(event),
		silent: shouldStartCallSilently(event),
	};
}
export async function checkAndStartCall(channelId: string, silent = false): Promise<boolean> {
	try {
		const {ringable} = await CallCommands.checkCallEligibility(channelId);
		if (!ringable) {
			ModalCommands.push(modal(() => <CallNotRingableModal />));
			return false;
		}
		CallCommands.startCall(channelId, silent);
		return true;
	} catch (error) {
		logger.error('Failed to check call eligibility:', error);
		return false;
	}
}
export async function requestStartCall(
	i18n: I18n,
	channelId: string,
	options: CallStartConfirmationOptions = {},
): Promise<void> {
	const {bypassConfirm = false, kind = 'voice', silent = false} = options;
	const shouldConfirm = Accessibility.confirmBeforeStartingCalls && !bypassConfirm;
	if (!shouldConfirm) {
		await checkAndStartCall(channelId, silent);
		return;
	}
	const isVideoCall = kind === 'video';
	const title = isVideoCall ? i18n._(msg`Start Video Call`) : i18n._(msg`Start Voice Call`);
	const description = isVideoCall
		? i18n._(msg`Are you sure you want to start a video call?`)
		: i18n._(msg`Are you sure you want to start a voice call?`);
	ModalCommands.push(
		modal(() => (
			<ConfirmModal
				title={title}
				description={description}
				primaryText={title}
				primaryVariant="primary"
				onPrimary={async () => {
					await checkAndStartCall(channelId, silent);
				}}
			/>
		)),
	);
}
